import { Injectable } from '@angular/core';
import { Observable, map } from 'rxjs';
import { EntityKeyByReference, GlobalEntityDefinition } from '@card-software/library';
import { TranslateService } from '@ngx-translate/core';
import { BaseTransactionStrategy } from './base-transaction.strategy';
import { SupplyWarehouseDocumentType } from '../../models/supply-warehouse/supply-warehouse-document-type.enum';
import { TableColumn } from '../../models/supply-warehouse/table-column.model';
import { TransactionAction } from '../../models/supply-warehouse/transaction-action.model';
import { ProviderReturn, ProviderReturnStatus } from '../../models/inventory/provider-return';
import { GoodsReceiptEntry } from '../../models/inventory/goods-receipt';
import { ProviderReturnProxyService } from '../../proxies/inventory/provider-return.proxy.service';
import { GoodsReceiptProxyService } from '../../proxies/inventory/goods-receipt.proxy.service';

/**
 * Estrategia para manejar Provider Returns (PR)
 * Documenta la devolución parcial de items a un proveedor desde un Goods Receipt Note
 */
@Injectable({
  providedIn: 'root',
})
export class ProviderReturnStrategy extends BaseTransactionStrategy<ProviderReturn, ProviderReturnStatus> {
  readonly documentType = SupplyWarehouseDocumentType.ProviderReturn;
  readonly initiatorName = 'Goods Receipt Note';
  readonly locationAssignmentType = 'source' as const;

  constructor(
    private providerReturnService: ProviderReturnProxyService,
    private goodsReceiptService: GoodsReceiptProxyService,
    private translateService: TranslateService,
  ) {
    super();
  }

  private translate(key: string, fallback: string): string {
    const result = this.translateService.instant(key);
    return result === key ? fallback : result;
  }

  loadDocument(id: string): Observable<ProviderReturn | null> {
    return this.providerReturnService.getById(id).pipe(map((response) => response.data || null));
  }

  loadInitiatorDocument(grnId: string): Observable<GoodsReceiptEntry | null> {
    return this.goodsReceiptService.getById(grnId).pipe(map((response) => response.data || null));
  }

  searchTransactionDocuments(grnId: string): Observable<ProviderReturn[]> {
    const entityKey = this.getInitiatorEntityKey(grnId);
    return this.providerReturnService.search({ initiator: entityKey }).pipe(map((response) => response.data?.items || []));
  }

  createFromInitiator(grnId: string): Observable<ProviderReturn> {
    const entityKey = this.getInitiatorEntityKey(grnId);
    return this.providerReturnService.createFromEntityKey(entityKey).pipe(map((response) => response.data!));
  }

  updateDocument(id: string, document: ProviderReturn): Observable<ProviderReturn> {
    return this.providerReturnService.update(id, document).pipe(map((response) => response.data!));
  }

  updateStatus(id: string, newStatus: ProviderReturnStatus): Observable<ProviderReturn> {
    return this.providerReturnService.updateStatus(id, newStatus).pipe(map((response) => response.data!));
  }

  getTableColumns(): TableColumn[] {
    return [
      { field: 'itemCode', headerKey: 'PR_Item_Code', header: 'Item Code', width: '150px', type: 'text', sortable: true },
      { field: 'itemDescription', headerKey: 'PR_Item_Description', header: 'Item Description', width: '250px', type: 'text' },
      { field: 'quantity', headerKey: 'PR_Quantity', header: 'Return Quantity', width: '120px', type: 'numeric', editable: true },
      { field: 'uom', headerKey: 'PR_UOM', header: 'UOM', width: '100px', type: 'text' },
      { field: 'sourceLocations', headerKey: 'PR_Source_Locations', header: 'Source Locations', type: 'locations', editable: true },
    ];
  }

  getInitiatorItemsColumns(): TableColumn[] {
    return [
      { field: 'itemCode', headerKey: 'PR_Item_Code', header: 'Item Code', width: '150px', sortable: true },
      { field: 'itemDisplayName', headerKey: 'PR_Item_Description', header: 'Item Description', width: '250px' },
      { field: 'quantity', headerKey: 'PR_Received_Qty', header: 'Received Qty', width: '120px', type: 'numeric' },
      { field: 'uom', headerKey: 'PR_UOM', header: 'UOM', width: '100px', type: 'text' },
      { field: 'returnedQty', headerKey: 'PR_Returned_Qty', header: 'Returned Qty', width: '120px', type: 'numeric' },
      { field: 'remaining', headerKey: 'PR_Remaining', header: 'Remaining', width: '120px', type: 'numeric' },
      { field: 'actions', header: '', width: '80px', type: 'actions' },
    ];
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  getAvailableActions(status: ProviderReturnStatus, _document: ProviderReturn): TransactionAction[] {
    const actions: TransactionAction[] = [];

    switch (status) {
      case ProviderReturnStatus.Processing:
        actions.push({
          id: 'complete',
          label: this.translate('Common_Complete', 'Complete'),
          icon: 'pi pi-check',
          severity: 'success',
          requiresConfirmation: true,
          confirmationMessage: this.translate(
            'PR_Confirm_Complete',
            'Are you sure you want to complete this Provider Return? Items will be removed from inventory.',
          ),
        });
        actions.push({
          id: 'cancel',
          label: this.translate('Common_Cancel', 'Cancel'),
          icon: 'pi pi-times',
          severity: 'danger',
          requiresConfirmation: true,
          confirmationMessage: this.translate('PR_Confirm_Cancel', 'Are you sure you want to cancel this Provider Return?'),
        });
        break;

      case ProviderReturnStatus.Completed:
      case ProviderReturnStatus.Cancelled:
        // Read-only, no actions
        break;
    }

    return actions;
  }

  canEditQuantities(status: ProviderReturnStatus): boolean {
    return status === ProviderReturnStatus.Processing;
  }

  canEditLines(status: ProviderReturnStatus): boolean {
    return status === ProviderReturnStatus.Processing;
  }

  validateForStatusChange(document: ProviderReturn, newStatus: ProviderReturnStatus): { valid: boolean; errors: string[] } {
    const errors: string[] = [];

    if (newStatus === ProviderReturnStatus.Completed) {
      if (!document.returnReason?.trim()) {
        errors.push(this.translate('PR_Error_No_Return_Reason', 'Return reason is required'));
      }
    }

    if (!document.entries || document.entries.length === 0) {
      errors.push(this.translate('PR_Error_No_Entries', 'Provider Return must have at least one entry'));
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    document.entries?.forEach((entry: any) => {
      if (entry.quantity <= 0) {
        errors.push(`${entry.itemCode}: ${this.translate('PR_Error_Invalid_Quantity', 'Invalid quantity')}`);
      }
    });

    if (newStatus === ProviderReturnStatus.Completed) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      document.entries?.forEach((entry: any) => {
        if (!entry.sourceLocations || entry.sourceLocations.length === 0) {
          errors.push(`${entry.itemCode}: ${this.translate('PR_Error_No_Source_Locations', 'Source locations must be assigned')}`);
        } else {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const totalInLocations = entry.sourceLocations.reduce((sum: number, loc: any) => sum + loc.quantity, 0);
          const diff = Math.abs(totalInLocations - entry.quantity);
          if (diff > 0.001) {
            errors.push(
              `${entry.itemCode}: ${this.translate('PR_Error_Location_Mismatch', 'Source location quantities do not match line quantity')}`,
            );
          }
        }
      });
    }

    return { valid: errors.length === 0, errors };
  }

  getDocumentTitle(document: ProviderReturn | null): string {
    if (!document) return this.translate('PR_New', 'New Provider Return');
    return document.number || this.translate('PR_Title', 'Provider Return');
  }

  getInitiatorEntityKey(grnId: string): EntityKeyByReference {
    return {
      reference: grnId,
      entityDefinitionCode: GlobalEntityDefinition.GoodsReceipt,
    };
  }

  override supportsReportDownload(): boolean {
    return true;
  }

  override canDownloadReport(status: ProviderReturnStatus): boolean {
    return status === ProviderReturnStatus.Processing || status === ProviderReturnStatus.Completed;
  }

  override downloadReport(documentId: string): Observable<Blob> {
    return this.providerReturnService.downloadReport(documentId);
  }
}
